/**
 * Sign in — a port of `docs/html/login.html`.
 *
 * The markup here deliberately mirrors the prototype element for element and
 * class for class (`auth-wrap`, `auth-visual`, `auth-panel`, `role-grid`,
 * `inp-group`, `sso-btn`, `otp-row`, …) because `src/styles/app.css` *is* the
 * prototype's stylesheet, copied verbatim. Inventing new class names here would
 * mean re-implementing a design that already exists, and drifting from it.
 *
 * What changes is the behaviour behind it. `POST /auth/login` answers 200 with
 * one of three outcomes, and the screen cannot know which applies until the
 * credential is submitted:
 *
 *   authenticated → route to the landing screen the user's roles name
 *   MFA required  → swap the credential step for the code entry
 *   SSO required  → the domain forbids passwords; offer the provider instead
 *
 * The prototype faked all of this on a timer and stored a pretend session in
 * `localStorage`. None of that survives; only the appearance does.
 */
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { signIn, loadSession, clearAuthError, clearChallenge } from './authSlice.js';
import { useAuth } from '../../hooks/useAuth.js';
import { landingRouteFor } from '../../routes/navModel.js';
import * as authApi from '../../api/auth.api.js';
import { ButtonSpinner } from '../../components/common/index.jsx';

/* -------------------------------------------------------------------------- */
/* Role registry — mirrors `CRM.ROLES` in the prototype's assets/js/app.js      */
/* -------------------------------------------------------------------------- */

/**
 * The four demo personas the prototype offered as sign-in shortcuts.
 *
 * These cards do not choose a role: roles come from the credential, resolved by
 * `iam.user_role` on the server. All a card does is prefill the email box, which
 * is exactly what the prototype did with `ROLES[k].demo.user`.
 *
 * `superadmin` points at the account `db/bootstrap.js` actually creates. The
 * other three are the prototype's fictional addresses and have no row in
 * `iam.app_user` yet, so they will fail authentication until those users exist.
 */
const ROLES = {
  rep: {
    key: 'rep',
    name: 'Sales Representative',
    short: 'Sales Rep',
    desc: 'Leads, deals, activities & quotes',
    icon: 'bi-person-badge-fill',
    grad: 'linear-gradient(135deg,#0ea5e9,#06b6d4)',
    demo: 'msarkar@entiovi.com',
  },
  manager: {
    key: 'manager',
    name: 'Sales Manager',
    short: 'Sales Manager',
    desc: 'Team pipeline, approvals & coaching',
    icon: 'bi-people-fill',
    grad: 'linear-gradient(135deg,#6366f1,#8b5cf6)',
    demo: 'tsaikh@entiovi.com',
  },
  exec: {
    key: 'exec',
    name: 'Management / Executive',
    short: 'Executive',
    desc: 'Revenue, forecast & company KPIs',
    icon: 'bi-graph-up-arrow',
    grad: 'linear-gradient(135deg,#10b981,#14b8a6)',
    demo: 'skabiraj@entiovi.com',
  },
  superadmin: {
    key: 'superadmin',
    name: 'Super Administrator',
    short: 'Super Admin',
    desc: 'Full system, security & config control',
    icon: 'bi-shield-lock-fill',
    grad: 'linear-gradient(135deg,#f43f5e,#ec4899)',
    demo: 'psardar@entiovi.com',
  },
};

const ROLE_ORDER = ['rep', 'manager', 'exec', 'superadmin'];

/** The federated options the prototype offered, in its layout: two up, then one wide. */
const SSO_PROVIDERS = [
  { key: 'microsoft', label: 'Microsoft', icon: 'bi-microsoft', color: '#0078d4', span: 6 },
  { key: 'google', label: 'Google', icon: 'bi-google', color: '#ea4335', span: 6 },
  { key: 'saml', label: 'Enterprise SSO (SAML / OIDC)', icon: 'bi-key-fill', color: 'var(--brand-500)', span: 12 },
];
const ROLE_STORE_KEY = 'entiovi.login.role';

/** Read the last role card the user picked. Storage can throw in private mode. */
function readLastRole() {
  try {
    const k = localStorage.getItem(ROLE_STORE_KEY);
    return ROLES[k] ? k : 'superadmin';
  } catch {
    return 'superadmin';
  }
}

/** Remember the role card, so returning to the screen keeps the prefill. */
function writeLastRole(key) {
  try {
    localStorage.setItem(ROLE_STORE_KEY, key);
  } catch {
    /* private mode / blocked storage — the prefill is a convenience, not state */
  }
}

const loginSchema = z.object({
  email: z.string().min(1, 'Enter your work email or mobile number.'),
  password: z.string().min(1, 'Enter your password.'),
  remember_me: z.boolean().optional(),
});

/* -------------------------------------------------------------------------- */
/* Screen                                                                      */
/* -------------------------------------------------------------------------- */

export default function LoginPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { status, error, mfaChallenge, ssoRedirect, isAuthenticated, landingScreen } = useAuth();

  const [role, setRole] = useState(readLastRole);
  const [showPassword, setShowPassword] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);

  const {
    register, handleSubmit, formState: { errors }, setError, setValue, getValues,
  } = useForm({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: ROLES[readLastRole()].demo, password: '', remember_me: true },
  });

  /* Once the session resolves, go where the user was heading — or to the landing
     screen their highest-priority role names. */
  useEffect(() => {
    if (!isAuthenticated) return;
    const intended = location.state?.from?.pathname;
    navigate(intended ?? landingRouteFor(landingScreen), { replace: true });
  }, [isAuthenticated, landingScreen, location.state, navigate]);

  useEffect(() => () => dispatch(clearAuthError()), [dispatch]);

  /**
   * Pick a persona card.
   *
   * Prefills the email box and nothing else — the server decides what this
   * person may actually do, from the roles attached to the credential.
   */
  const selectRole = (key) => {
    setRole(key);
    writeLastRole(key);
    setValue('email', ROLES[key].demo, { shouldValidate: false });
    dispatch(clearAuthError());
  };

  /**
   * Submit the credential.
   *
   * Field-level failures from the API are mapped back onto their inputs;
   * everything else stays in the banner. `invalid_credentials` deliberately goes
   * to the banner rather than a field — the API will not say which of the two
   * was wrong, and guessing in the UI would give that away.
   */
  const onSubmit = async (values) => {
    const result = await dispatch(signIn(values));
    if (signIn.rejected.match(result)) {
      for (const detail of result.payload?.details ?? []) {
        if (detail.field === 'email' || detail.field === 'password') {
          setError(detail.field, { message: detail.message ?? detail.code });
        }
      }
      return;
    }
    if (result.payload.outcome === 'authenticated') {
      // Login gives enough to route; /auth/me fills in screens and field masks.
      dispatch(loadSession());
    }
  };

  const busy = status === 'signing-in';

  return (
    <div className="auth-wrap">
      <BrandPanel />

      <section className="auth-panel">
        <div className="w-100" style={{ maxWidth: 404, margin: '0 auto' }}>
          {mfaChallenge ? (
            <MfaStep
              challenge={mfaChallenge}
              email={getValues('email')}
              roleName={ROLES[role].short}
              onBack={() => dispatch(clearChallenge())}
            />
          ) : ssoRedirect ? (
            <SsoStep redirect={ssoRedirect} onBack={() => dispatch(clearChallenge())} />
          ) : (
            <div className="fade-up">
              {/* Brand repeats on narrow screens, where the visual panel collapses.
                  The wordmark itself is cut for the dark hero panel (BrandPanel below),
                  so it needs its own dark chip here to stay legible on this light background. */}
              <div className="d-lg-none mb-4">
                <div className="brand-logo-chip">
                  <img src="/logo-full.png" alt="EnGage — Sales | Marketing | Growth" className="brand-logo-full" />
                </div>
              </div>

              <div className="eyebrow mb-2">Welcome back</div>
              <h2 className="fw-750 mb-1" style={{ letterSpacing: '-.035em' }}>
                Sign in to your workspace
              </h2>
              <p className="t-sm ink-3 mb-4">
                Choose how you work — your dashboard, menu and permissions adapt to it.
              </p>

              <label className="lbl d-flex align-items-center justify-content-between">
                <span>I&rsquo;m signing in as</span>
                <span className="t-2xs ink-4 fw-450">Prefills the email</span>
              </label>
              <div className="role-grid mb-3" role="radiogroup" aria-label="Select your role">
                {ROLE_ORDER.map((key) => {
                  const r = ROLES[key];
                  const on = role === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      className={`role-card${on ? ' selected' : ''}`}
                      role="radio"
                      aria-checked={on}
                      onClick={() => selectRole(key)}
                    >
                      <div className="r-check"><i className="bi bi-check-lg" /></div>
                      <div className="r-ico" style={{ background: r.grad }}>
                        <i className={`bi ${r.icon}`} />
                      </div>
                      <div className="r-name">{r.short}</div>
                      <div className="r-desc">{r.desc}</div>
                    </button>
                  );
                })}
              </div>

              <form onSubmit={handleSubmit(onSubmit)} noValidate>
                <div className="mb-3">
                  <label className="lbl" htmlFor="email">Work email or mobile</label>
                  <div className="inp-group">
                    <input
                      type="text"
                      className={`form-control${errors.email ? ' is-invalid' : ''}`}
                      id="email"
                      autoComplete="username"
                      placeholder="you@company.com"
                      {...register('email')}
                    />
                    <i className="bi bi-envelope inp-ico" />
                  </div>
                  {errors.email ? (
                    <div className="t-2xs mt-1" style={{ color: 'var(--rose)' }}>
                      {errors.email.message}
                    </div>
                  ) : (
                    <div className="t-2xs ink-4 mt-1">
                      Demo account for <span className="fw-650 ink-2">{ROLES[role].name}</span> prefilled.
                    </div>
                  )}
                </div>

                <div className="mb-2">
                  <div className="d-flex align-items-center justify-content-between">
                    <label className="lbl mb-0" htmlFor="password">Password</label>
                    <button
                      type="button"
                      className="btn btn-link p-0 t-xs fw-600"
                      onClick={() => setResetOpen(true)}
                    >
                      Forgot?
                    </button>
                  </div>
                  <div className="inp-group mt-1">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      className={`form-control has-r${errors.password ? ' is-invalid' : ''}`}
                      id="password"
                      autoComplete="current-password"
                      placeholder="••••••••••••"
                      {...register('password')}
                    />
                    <i className="bi bi-lock inp-ico" />
                    <i
                      className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'} inp-ico inp-ico-r`}
                      role="button"
                      tabIndex={0}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      onClick={() => setShowPassword((s) => !s)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setShowPassword((s) => !s);
                        }
                      }}
                    />
                  </div>
                  {errors.password && (
                    <div className="t-2xs mt-1" style={{ color: 'var(--rose)' }}>
                      {errors.password.message}
                    </div>
                  )}
                </div>

                <div className="d-flex align-items-center justify-content-between mb-3">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="remember"
                      {...register('remember_me')}
                    />
                    <label className="form-check-label" htmlFor="remember">
                      Remember this device
                    </label>
                  </div>
                  <span className="pill pill-gray" title="Multi-factor authentication is enforced by policy">
                    <i className="bi bi-shield-lock-fill" /> MFA on
                  </span>
                </div>

                {error && (
                  <div className="alert alert-danger py-2 px-3 t-xs mb-3" role="alert">
                    <i className="bi bi-exclamation-circle me-1" />
                    {error.message}
                    {error.code === 'account_locked' && error.payload?.locked_until && (
                      <div className="mt-1">
                        Try again after {new Date(error.payload.locked_until).toLocaleTimeString()}.
                      </div>
                    )}
                  </div>
                )}

                <button
                  type="submit"
                  className="btn btn-brand btn-lg-x w-100 d-flex align-items-center justify-content-center gap-2"
                  disabled={busy}
                >
                  {busy ? (
                    <><ButtonSpinner className="me-1" /> Verifying…</>
                  ) : (
                    <><span className="btn-label">Continue</span><i className="bi bi-arrow-right" /></>
                  )}
                </button>

                <div className="divider-x my-3">or continue with</div>

                {/* Disabled, not hidden. The options are real and specified in API §2.6;
                    the handshake is simply not built, so a click would 501. Leaving
                    them live would look like a broken login rather than a pending
                    feature — the tooltip carries the reason without adding a
                    paragraph to the screen. */}
                <div className="row g-2">
                  {SSO_PROVIDERS.map(({ key, label, icon, color, span }) => (
                    <div className={`col-${span}`} key={key}>
                      <button
                        type="button"
                        className="sso-btn"
                        disabled
                        title="Federated sign-in is specified in API §2.6 but not implemented in this build."
                      >
                        <i className={`bi ${icon}`} style={{ color }} /> {label}
                      </button>
                    </div>
                  ))}
                </div>
              </form>
            </div>
          )}

          <div className="text-center t-2xs ink-4 mt-4 pt-2">
            Protected by TLS 1.3 · SSO · MFA · session timeout policies<br />
            <a href="#privacy" className="ink-4">Privacy</a> ·{' '}
            <a href="#terms" className="ink-4">Terms</a> ·{' '}
            <a href="#status" className="ink-4">Status</a>
          </div>
        </div>
      </section>

      <ResetPasswordModal open={resetOpen} onClose={() => setResetOpen(false)} />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Left panel                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * The brand / value panel.
 *
 * Purely decorative, and the figures in it are illustrative rather than read
 * from any tenant — this renders before anyone has authenticated, so there is
 * no organisation to report on yet.
 */
function BrandPanel() {
  return (
    <section className="auth-visual d-flex flex-column">
      <div className="mb-auto">
        <img src="/logo-full.png" alt="EnGage — Sales | Marketing | Growth" className="brand-logo-hero" />
      </div>

      <div className="my-4 my-lg-5" style={{ maxWidth: 560 }}>
        <span className="pill glass text-white mb-3" style={{ borderColor: 'rgba(255,255,255,.22)' }}>
          <i className="bi bi-stars" /> AI-assisted selling, built in
        </span>
        <h1
          className="text-white fw-750 mb-3"
          style={{ fontSize: 'clamp(26px,3.4vw,42px)', lineHeight: 1.14, letterSpacing: '-.035em' }}
        >
          Every lead, deal and follow-up —<br className="d-none d-xl-block" />
          <span
            style={{
              background: 'linear-gradient(100deg,#a5b4fc,#f9a8d4)',
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            on one 360° platform.
          </span>
        </h1>
        <p className="t-md mb-0" style={{ color: 'rgba(232,234,246,.78)', maxWidth: 460 }}>
          Pipeline visibility, quota tracking, quotation approvals and intelligent
          forecasting for sales reps, managers and executives.
        </p>
      </div>

      <div className="row g-2 g-lg-3 mb-4" style={{ maxWidth: 600 }}>
        {[
          ['$4.8M', 'Weighted pipeline'],
          ['32%', 'Avg. win rate'],
          ['12k+', 'Sellers onboard'],
        ].map(([value, label]) => (
          <div className="col-4" key={label}>
            <div className="glass p-3">
              <div className="t-2xl fw-750 text-white lh-1">{value}</div>
              <div className="t-2xs mt-1" style={{ color: 'rgba(232,234,246,.6)' }}>{label}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-auto">
        <div className="t-2xs tracking-wide text-uppercase mb-2" style={{ color: 'rgba(232,234,246,.45)' }}>
          Trusted by revenue teams at
        </div>
        <div className="logo-row text-white">
          <span><i className="bi bi-hexagon-fill me-1" />Northwind</span>
          <span><i className="bi bi-triangle-fill me-1" />Vertex</span>
          <span><i className="bi bi-circle-fill me-1" />Aurora</span>
          <span><i className="bi bi-diamond-fill me-1" />Helix</span>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* Steps                                                                       */
/* -------------------------------------------------------------------------- */

/**
 * The second factor — six digit boxes, as in the prototype.
 *
 * Unlike the prototype the countdown is real: the challenge expires server-side
 * on `expires_in`, and every wrong code burns one of its attempts. Verification
 * currently answers 501 because no secrets manager is configured, and the notice
 * at the foot says so rather than letting a correct code look wrong.
 */
function MfaStep({ challenge, email, roleName, onBack }) {
  const boxCount = 6;
  const [digits, setDigits] = useState(() => Array(boxCount).fill(''));
  const [factorId, setFactorId] = useState(challenge.factors?.[0]?.id ?? '');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(challenge.expiresIn ?? 300);
  const inputs = useRef([]);

  useEffect(() => {
    inputs.current[0]?.focus();
  }, []);

  useEffect(() => {
    if (secondsLeft <= 0) return undefined;
    const timer = setInterval(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearInterval(timer);
  }, [secondsLeft]);

  const expired = secondsLeft <= 0;
  const code = digits.join('');

  /** Send the assembled code. Split out so both the button and auto-submit use it. */
  const verify = async (value) => {
    setBusy(true);
    setFailure(null);
    try {
      await authApi.verifyMfa({
        challenge_token: challenge.challengeToken,
        factor_id: factorId,
        code: value,
      });
      window.location.reload(); // a fresh boot picks up the new session cleanly
    } catch (err) {
      setFailure(err);
      setDigits(Array(boxCount).fill(''));
      inputs.current[0]?.focus();
    } finally {
      setBusy(false);
    }
  };

  /** Type a digit: keep one character, advance, and submit once all six are in. */
  const onDigit = (index, raw) => {
    const value = raw.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[index] = value;
    setDigits(next);
    if (value && index < boxCount - 1) inputs.current[index + 1]?.focus();
    if (next.every(Boolean)) setTimeout(() => verify(next.join('')), 120);
  };

  /** Backspace walks back through empty boxes; arrows move without editing. */
  const onKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) inputs.current[index - 1]?.focus();
    if (e.key === 'ArrowLeft' && index > 0) inputs.current[index - 1]?.focus();
    if (e.key === 'ArrowRight' && index < boxCount - 1) inputs.current[index + 1]?.focus();
  };

  /** Pasting the whole code from an SMS or authenticator fills every box at once. */
  const onPaste = (e) => {
    const pasted = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, boxCount);
    if (!pasted) return;
    e.preventDefault();
    const next = Array(boxCount).fill('');
    pasted.split('').forEach((d, i) => { next[i] = d; });
    setDigits(next);
    inputs.current[Math.min(pasted.length, boxCount - 1)]?.focus();
    if (pasted.length === boxCount) setTimeout(() => verify(pasted), 120);
  };

  return (
    <div className="fade-up">
      <button className="btn btn-ghost btn-sm-x mb-4" onClick={onBack} type="button">
        <i className="bi bi-arrow-left me-1" />Back
      </button>

      <div
        className="d-flex align-items-center justify-content-center mx-auto mb-3"
        style={{ width: 52, height: 52, borderRadius: 16, background: 'var(--grad-brand)', boxShadow: 'var(--shadow-brand)' }}
      >
        <i className="bi bi-shield-lock-fill text-white" style={{ fontSize: 22 }} />
      </div>

      <h2 className="fw-750 text-center mb-1" style={{ letterSpacing: '-.035em' }}>
        Two-factor verification
      </h2>
      <p className="t-sm ink-3 text-center mb-4">
        Enter the 6-digit code sent to <span className="fw-650 ink-2">{email}</span>
      </p>

      {challenge.factors?.length > 1 && (
        <div className="mb-3">
          <label className="lbl" htmlFor="factor">Method</label>
          <select
            id="factor"
            className="form-select"
            value={factorId}
            onChange={(e) => setFactorId(e.target.value)}
          >
            {challenge.factors.map((f) => (
              <option key={f.id} value={f.id}>{f.label ?? f.kind}</option>
            ))}
          </select>
        </div>
      )}

      <div className="otp-row justify-content-center mb-3" onPaste={onPaste}>
        {digits.map((d, i) => (
          <input
            /* eslint-disable-next-line react/no-array-index-key -- fixed-length positional boxes */
            key={i}
            ref={(el) => { inputs.current[i] = el; }}
            inputMode="numeric"
            maxLength={1}
            aria-label={`Digit ${i + 1}`}
            className={d ? 'filled' : undefined}
            value={d}
            disabled={expired || busy}
            onChange={(e) => onDigit(i, e.target.value)}
            onKeyDown={(e) => onKeyDown(i, e)}
          />
        ))}
      </div>

      <div className="text-center t-xs ink-3 mb-4">
        {expired ? (
          <span className="text-danger">This challenge has expired — go back and sign in again.</span>
        ) : (
          <>
            Code expires in{' '}
            <span className="fw-650 ink-2">
              {Math.floor(secondsLeft / 60)}:{String(secondsLeft % 60).padStart(2, '0')}
            </span>
          </>
        )}
      </div>

      {failure && (
        <div className="alert alert-danger py-2 px-3 t-xs mb-3" role="alert">
          <i className="bi bi-exclamation-circle me-1" />{failure.message}
        </div>
      )}

      <button
        className="btn btn-brand btn-lg-x w-100 mb-2"
        onClick={() => verify(code)}
        disabled={busy || expired || code.length < boxCount}
        type="button"
      >
        {busy ? <><ButtonSpinner />Signing in…</> : 'Verify & sign in'}
      </button>
      <button className="btn btn-ghost btn-lg-x w-100 t-sm" type="button" disabled>
        Use an authenticator app instead
      </button>

      <div
        className="d-flex align-items-start gap-2 mt-4 p-3"
        style={{ background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 'var(--r)' }}
      >
        <i className="bi bi-info-circle text-primary mt-1" />
        <div className="t-xs ink-3">
          MFA verification returns <span className="fw-700 ink-2">501</span> in this build — no
          secrets manager is configured, so no code can be checked. The{' '}
          <span className="fw-700 ink-2">{roleName}</span> challenge is real; only the verifier is missing.
        </div>
      </div>
    </div>
  );
}

/**
 * The account's domain is configured for single sign-on, so a password will not
 * be accepted at all. Saying that outright beats a password that silently never works.
 */
function SsoStep({ redirect, onBack }) {
  return (
    <div className="fade-up">
      <button className="btn btn-ghost btn-sm-x mb-4" onClick={onBack} type="button">
        <i className="bi bi-arrow-left me-1" />Back
      </button>

      <div
        className="d-flex align-items-center justify-content-center mx-auto mb-3"
        style={{ width: 52, height: 52, borderRadius: 16, background: 'var(--grad-brand)', boxShadow: 'var(--shadow-brand)' }}
      >
        <i className="bi bi-key-fill text-white" style={{ fontSize: 22 }} />
      </div>

      <h2 className="fw-750 text-center mb-1" style={{ letterSpacing: '-.035em' }}>
        Continue with {redirect.provider.display_name}
      </h2>
      <p className="t-sm ink-3 text-center mb-4">
        Your organisation requires single sign-on for this domain, so password sign-in is disabled.
      </p>

      <a className="btn btn-brand btn-lg-x w-100 mb-2" href={redirect.authorizeUrl}>
        <i className="bi bi-shield-lock me-2" />
        Continue with {redirect.provider.display_name}
      </a>
      <button className="btn btn-ghost btn-lg-x w-100 t-sm" type="button" onClick={onBack}>
        Use a different account
      </button>

      <div
        className="d-flex align-items-start gap-2 mt-4 p-3"
        style={{ background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 'var(--r)' }}
      >
        <i className="bi bi-info-circle text-primary mt-1" />
        <div className="t-xs ink-3">
          The SSO handshake is not implemented in this build — the button returns a 501.
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Reset modal                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Password reset request, as the prototype's `#resetModal`.
 *
 * Driven by React state rather than Bootstrap's modal JS, so the bundle does not
 * need `bootstrap.bundle.js` for one dialog. The classes are Bootstrap's, so it
 * looks identical.
 *
 * `POST /auth/password/forgot` now answers `404 email_not_found` for an
 * address with no reset-eligible account, so `failure.message` below already
 * carries that copy — a deliberate product choice to tell the caller
 * outright rather than the enumeration-safe uniform 202 `docs/API.md` §2
 * specifies. See `auth.service.js`'s `forgotPassword` doc comment.
 */
function ResetPasswordModal({ open, onClose }) {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [failure, setFailure] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    const onEsc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [open, onClose]);

  if (!open) return null;

  const submit = async () => {
    setBusy(true);
    setFailure(null);
    try {
      await authApi.forgotPassword(email);
      setSent(true);
    } catch (err) {
      setFailure(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="modal-backdrop fade show" onClick={onClose} />
      <div className="modal fade show d-block" tabIndex={-1} role="dialog">
        <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable" style={{ maxWidth: 420 }}>
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Reset your password</h5>
              <button type="button" className="btn-close btn-sm" aria-label="Close" onClick={onClose} />
            </div>
            <div className="modal-body">
              {sent ? (
                <p className="t-sm ink-3 mb-0">
                  <i className="bi bi-check-circle-fill text-success me-1" />
                  A single-use reset link is on its way to <span className="fw-650 ink-2">{email}</span>.
                  It is valid for 30 minutes.
                </p>
              ) : (
                <>
                  <p className="t-sm ink-3">
                    Enter your work email. We&rsquo;ll send a secure, single-use reset link valid for
                    30 minutes.
                  </p>
                  <label className="lbl" htmlFor="resetEmail">Work email</label>
                  <div className="inp-group">
                    <input
                      type="email"
                      className="form-control"
                      id="resetEmail"
                      placeholder="you@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                    <i className="bi bi-envelope inp-ico" />
                  </div>
                  {failure && (
                    <div className="alert alert-danger py-2 px-3 t-xs mt-3 mb-0" role="alert">
                      {failure.message}
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-cancel btn-sm-x" onClick={onClose} type="button">
                {sent ? 'Close' : 'Cancel'}
              </button>
              {!sent && (
                <button
                  className="btn btn-save btn-sm-x"
                  onClick={submit}
                  disabled={busy || !email}
                  type="button"
                >
                  {busy ? 'Sending…' : 'Send reset link'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Shared frame                                                                */
/* -------------------------------------------------------------------------- */

/**
 * The split frame every unauthenticated screen uses.
 *
 * Exported so `ForgotPasswordPage` and any future reset/invite screen sit in the
 * same two-panel layout as sign-in instead of re-deriving it.
 */
export function AuthLayout({ children }) {
  return (
    <div className="auth-wrap">
      <BrandPanel />
      <section className="auth-panel">
        <div className="w-100" style={{ maxWidth: 404, margin: '0 auto' }}>
          <div className="fade-up">{children}</div>
        </div>
      </section>
    </div>
  );
}
